import { lazy, Suspense } from "react";
import { Link, NavLink, Outlet, useMatch } from "react-router";
import { useMutation } from "@tanstack/react-query";
import { Logo, LogoMark } from "@baze/brand";
import { ClipboardList, LayoutGrid, LayoutTemplate, MailWarning, WifiOff } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { api } from "@/lib/api";
import { useOnline } from "@/lib/hooks";
import { cn } from "@/lib/cn";
import { t } from "@/i18n/id";
import { AnnouncementBanner } from "./AnnouncementBanner";
import { HelpButton } from "./HelpButton";
import { AvatarFallback, SwitcherFallback } from "./fallbacks";

// Dropdown menus (radix menu + floating-ui) load right after first paint, off the entry chunk.
const UserMenu = lazy(() => import("./UserMenu"));
const SiteSwitcher = lazy(() => import("./SiteSwitcher"));
import { CartButton } from "./CartButton";

function VerifyEmailBanner() {
  const { user } = useAuth();
  const resend = useMutation({ mutationFn: () => api.auth.resendVerification(), meta: { success: t.auth.verifyResent } });
  if (!user || user.emailVerified) return null;
  return (
    <div className="border-b border-amber-600/15 bg-warning-soft/60 px-4 py-2 text-[13px] text-amber-950">
      <div className="mx-auto flex max-w-[1120px] flex-wrap items-center gap-x-3 gap-y-1">
        <MailWarning className="size-4 shrink-0" aria-hidden="true" />
        <span className="font-medium">{t.auth.verifyBanner}</span>
        <button type="button" className="min-h-8 font-semibold underline underline-offset-2 disabled:opacity-60" disabled={resend.isPending || resend.isSuccess} onClick={() => resend.mutate()}>
          {resend.isSuccess ? t.auth.verifyResent : t.auth.verifyResend}
        </button>
      </div>
    </div>
  );
}

function OfflineBanner() {
  const online = useOnline();
  if (online) return null;
  return (
    <div role="status" className="flex items-center justify-center gap-2 bg-slate-900 px-4 py-2 text-[13px] font-medium text-white">
      <WifiOff className="size-4" aria-hidden="true" />
      {t.common.offlineBanner}
    </div>
  );
}

const navCls = ({ isActive }: { isActive: boolean }) =>
  cn(
    "inline-flex h-9 items-center gap-1.5 rounded-pill px-3.5 text-sm font-semibold transition-colors [&_svg]:size-4",
    isActive ? "bg-primary-soft text-primary" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
  );

export function TopBar() {
  const siteMatch = useMatch("/situs/:id/*");
  const siteId = siteMatch?.params.id;
  return (
    <header className="safe-top sticky top-0 z-40 border-b border-line bg-white">
      <div className="container-shell flex h-16 items-center gap-2">
        <Link to="/situs" className="flex shrink-0 items-center rounded-lg" aria-label={t.brand.product}>
          <span className={cn(siteId ? "hidden md:inline-flex" : "hidden xs:inline-flex")}>
            <Logo />
          </span>
          <span className={cn(siteId ? "md:hidden" : "xs:hidden", "grid size-8 place-items-center")}>
            <LogoMark className="size-8" aria-hidden="true" title="" />
          </span>
        </Link>
        {siteId ? (
          <>
            <span className="text-slate-300" aria-hidden="true">
              /
            </span>
            <Suspense fallback={<SwitcherFallback siteId={siteId} />}>
              <SiteSwitcher siteId={siteId} />
            </Suspense>
          </>
        ) : null}
        <nav aria-label={t.shell.mainNav} className="ml-4 hidden items-center gap-1 lg:flex">
          <NavLink to="/situs" end className={navCls}>
            <LayoutGrid aria-hidden />
            {t.shell.mySites}
          </NavLink>
          <NavLink to="/template" className={navCls}>
            <LayoutTemplate aria-hidden />
            {t.library.nav}
          </NavLink>
          <NavLink to="/pesanan" className={navCls}>
            <ClipboardList aria-hidden />
            {t.shell.orders}
          </NavLink>
        </nav>
        <div className="ml-auto flex items-center gap-2">
          {siteId ? <CartButton siteId={siteId} /> : null}
          <HelpButton />
          <Suspense fallback={<AvatarFallback />}>
            <UserMenu />
          </Suspense>
        </div>
      </div>
    </header>
  );
}

export function AppShell() {
  return (
    <div className="bg-glow flex min-h-dvh flex-col bg-paper">
      <a href="#konten" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[70] focus:rounded-pill focus:bg-white focus:px-4 focus:py-2 focus:shadow-card">
        {t.common.skipToContent}
      </a>
      <AnnouncementBanner />
      <OfflineBanner />
      <TopBar />
      <VerifyEmailBanner />
      <main id="konten" tabIndex={-1} className="flex-1 outline-none">
        <Outlet />
      </main>
    </div>
  );
}
